import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";

import {
  fetchOrgChart,
  fetchOrgChartRevision,
  exportOrgChartRevisionPDF,
} from "../../../../api/quality/orgCharts";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";

export const OrgChartView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canvasRef = useRef(null);
  const [orgChart, setOrgChart] = useState(null);
  const [revision, setRevision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const orgChartData = await fetchOrgChart(id);
      setOrgChart(orgChartData);

      // Only load approved revision - if no approved revision exists, show error
      if (!orgChartData.approved_revision) {
        setError("No approved revision available");
        setLoading(false);
        return;
      }

      const revisionData = await fetchOrgChartRevision(
        id,
        orgChartData.approved_revision.id
      );
      setRevision(revisionData);
      setNodes(revisionData.nodes || []);
      setLinks(revisionData.links || []);

      // Check if there's a newer approved revision (this one is outdated)
      if (orgChartData.latest_revision && 
          orgChartData.latest_revision.id !== orgChartData.approved_revision.id &&
          orgChartData.latest_revision.is_approved) {
        // This revision is outdated, but we still show it
      }
    } catch (err) {
      console.error("Error loading org chart:", err);
      setError("Failed to load org chart");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (isExportingPDF) return; // Prevent multiple clicks
    
    if (!revision || !revision.is_approved) {
      toast.error("Kun godkjente revisjoner kan eksporteres");
      return;
    }

    setIsExportingPDF(true);
    
    try {
      const result = await exportOrgChartRevisionPDF(id, revision.id);
      
      if (result.data) {
        // Validate PDF blob
        if (result.data.size < 100) {
          throw new Error("PDF-filen er for liten eller ugyldig");
        }
        
        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `organisasjonskart_${id}_revisjon_${revision.revision_number}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success("PDF ble eksportert");
      } else {
        const errorMsg = result.errors?.join(", ") || "Kunne ikke eksportere PDF";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(error.message || "Kunne ikke eksportere PDF");
    } finally {
      setIsExportingPDF(false);
    }
  };

  const getNodeById = (nodeId) => {
    return nodes.find((n) => n.id === nodeId);
  };

  const renderLink = (link) => {
    const fromNode = getNodeById(link.from_node_id);
    const toNode = getNodeById(link.to_node_id);
    if (!fromNode || !toNode) return null;

    const fromX = fromNode.x_position + fromNode.width / 2;
    const fromY = fromNode.y_position + fromNode.height;
    const toX = toNode.x_position + toNode.width / 2;
    const toY = toNode.y_position;

    return (
      <g key={link.id}>
        <line
          x1={fromX}
          y1={fromY}
          x2={toX}
          y2={toY}
          stroke={link.color}
          strokeWidth={2}
          markerEnd="url(#arrowhead)"
        />
      </g>
    );
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[error]} />;
  }

  if (!orgChart) {
    return <ResourceError errors={[t("Organisasjonskart ikke funnet")]} />;
  }

  return (
    <>
      <DashboardHeader
        title={orgChart.title}
        subtitle={orgChart.description || ""}
        mainButton={{
          label: t("Tilbake"),
          action: () => navigate(-1),
          path: null,
          color: "",
          icon: ArrowLeftIcon,
        }}
        actions={[
          {
            label: isExportingPDF ? t("Eksporterer...") : t("Eksporter PDF"),
            action: handleExportPDF,
            icon: PrinterIcon,
            hidden: false,
            menu: false
          },
        ]}
      >
        <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Revision Info */}
          {revision && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-base font-semibold text-gray-900">
                      {t("Revisjon")} {revision.revision_number}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      {t("Godkjent")}
                    </span>
                    {orgChart.latest_revision && 
                     orgChart.latest_revision.id !== revision.id &&
                     orgChart.latest_revision.is_approved && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {t("Utgått")}
                      </span>
                    )}
                  </div>
                </div>
                {revision.approved_at && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{t("Godkjent")}:</span>{" "}
                    {new Date(revision.approved_at).toLocaleString("no-NO", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                )}
                {orgChart.latest_revision && 
                 orgChart.latest_revision.id !== revision.id &&
                 orgChart.latest_revision.is_approved && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-sm text-yellow-800">
                      {t("Denne revisjonen er godkjent, men det finnes en nyere godkjent revisjon (Revisjon")} {orgChart.latest_revision.revision_number}{t("). Denne revisjonen er ikke lenger gjeldende.")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Canvas */}
          <div className="bg-white rounded-lg shadow p-6">
            <div
              ref={canvasRef}
              className="border-2 border-gray-300 rounded-lg relative bg-gray-50"
              style={{ width: "100%", height: "800px", overflow: "auto" }}
            >
              <svg
                width="100%"
                height="100%"
                style={{ position: "absolute", top: 0, left: 0 }}
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3, 0 6" fill="#6B7280" />
                  </marker>
                </defs>
                {links.map((link) => renderLink(link))}
              </svg>

              {nodes.map((node) => (
                <div
                  key={node.id}
                  style={{
                    position: "absolute",
                    left: node.x_position,
                    top: node.y_position,
                    width: node.width,
                    height: node.height,
                    backgroundColor: node.color,
                    border: "2px solid #1E40AF",
                    borderRadius: "8px",
                    padding: "8px",
                    zIndex: node.z_index,
                  }}
                >
                  <div className="text-white font-medium text-sm mb-1">
                    {node.title}
                  </div>
                  {node.description && (
                    <div className="text-white text-xs opacity-90">
                      {node.description}
                    </div>
                  )}
                  {node.user && (
                    <div className="text-white text-xs mt-1 opacity-75">
                      {node.user.displayTitle}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DashboardHeader>
    </>
  );
};

